import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { AsyncPipe, NgOptimizedImage } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  map,
  NEVER,
  of,
  startWith,
  switchMap,
  tap
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SupportContactLookupsService } from './services/support-contact-lookups.service';
import { BranchOffice, LookupOption, OfficeOption, SupportContactLookups } from './models/support-contact-lookups';
import { CreateDepartmentContactRequest } from './models/create-department-contact-request';
import { AssignableParty, isAssignable } from './models/assignable-party';
import { ContactsService } from '../../services/contacts.service';
import { PartyService } from '../../../users/components/user-details/services/party.service';
import { Contact } from '../../models/contacts';
import { EditContactDetails } from '../edit-contact/models/edit-contact';
import { ContactDetails } from '../contact-details/models/contact-details';
import { TransportModeIconPipe } from '../../../../../../pipes/transport-mode-icon.pipe';
import { PhoneInputComponent } from '../../../../../../components/phone-input/phone-input.component';
import { EMPTY_PHONE_VALUE, PhoneValue } from '../../../../../../components/phone-input/phone-value';
import { ErrorHandlerService } from '../../../../../../utils/cross-xss-script-error-handler-service';

// The Department fields the search row already carries (see Contact in models/contacts.ts) —
// enough to seed the name builder and description without an extra request. Everything else
// (email, phone, assignments, ...) still comes from GET /supportContacts/{id}, same as the
// Person edit dialog uses — see AddDepartmentContactComponent.loadForEdit().
export type DepartmentEditSeed = Pick<Contact, 'id' | 'productId' | 'functionId' | 'branchOfficeId' | 'description'>;

// Existing display names, passed in by ContactsComponent from the contact set it has already
// fetched, so the "already exists" check costs no extra request.
export interface AddDepartmentContactData {
  existingNames: string[];
  // Present only when this dialog is reused to edit an existing Department contact — see
  // ContactsComponent.editContact() and AddDepartmentContactComponent.isEditMode.
  editContact?: DepartmentEditSeed;
  // Present only when opened from the Organisations Detail screen's "Create a new
  // department…" fallback (see DepartmentsSectionComponent) — pre-assigns the new contact to
  // that organisation. Ignored in edit mode.
  preselectedParty?: AssignableParty;
}

// The grid columns a Department edit can actually change. Sent back on a successful update so
// ContactsComponent can patch the one row in place instead of refetching the whole grid (which
// would also reset scroll position — see ContactsComponent.patchContact()).
export type DepartmentContactPatch = Pick<
  Contact,
  | 'id'
  | 'displayName'
  | 'email'
  | 'phone'
  | 'phoneCountryId'
  | 'mobilePhone'
  | 'mobilePhoneCountryId'
  | 'isActive'
  | 'productId'
  | 'functionId'
  | 'branchOfficeId'
  | 'description'
>;

export type AddDepartmentContactResult =
  | { action: 'Added'; contact: ContactDetails }
  | { action: 'Updated'; contact: DepartmentContactPatch };

interface PartySearchState {
  parties: AssignableParty[];
  loading: boolean;
  searched: boolean;
  error: boolean;
}

const EMPTY_PARTY_SEARCH: PartySearchState = { parties: [], loading: false, searched: false, error: false };

// Matches the debounce the older contact-details dialog uses against the same /parties endpoint.
const PARTY_SEARCH_DEBOUNCE_MS = 500;

// Below this the search doesn't fire — /parties is a server-side lookup over a large set and
// one- or two-character terms match almost everything. Mirrors PartySelectorComponent.
const MIN_PARTY_SEARCH_LENGTH = 3;

// Like the Add Person Contact dialog, this deliberately bypasses Material's form-field/button
// theming in favour of plain elements + bespoke CSS, to match the design spec exactly.
//
// Function is a native <select> — the spec draws it as a plain chevron dropdown and the list is
// a handful of entries, so native gives keyboard support and mobile pickers for free. Office and
// Product are both custom listboxes instead: Office because there are too many branch offices to
// scroll (a search box over a listbox styled to read like the native picker it replaced — bold
// country headers, indented cities), Product because each option needs an icon, which no
// browser can render inside a native <option>. Both replace the arrow/Enter/Escape handling
// native would otherwise have provided.
@Component({
  selector: 'app-add-department-contact',
  imports: [
    AsyncPipe,
    NgOptimizedImage,
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatIcon,
    MatProgressSpinner,
    TransportModeIconPipe,
    PhoneInputComponent
  ],
  templateUrl: './add-department-contact.component.html',
  styleUrl: './add-department-contact.component.scss'
})
export class AddDepartmentContactComponent implements OnInit {
  departmentForm = new FormGroup({
    productId: new FormControl<number | null>(null),
    functionId: new FormControl<number | null>(null),
    // Required even when a custom name is used: the office is what sets the address.
    branchOfficeId: new FormControl<number | null>(null, { validators: Validators.required }),
    useCustomName: new FormControl(false, { nonNullable: true }),
    customName: new FormControl('', { nonNullable: true }),
    // Not required on its own — save() also enforces that at least one of email/phone/
    // mobilePhone is present (see showContactMethodError), matching the API.
    email: new FormControl('', { validators: Validators.email, nonNullable: true }),
    // Each has its own country (PhoneInputComponent), so Business Phone and Mobile Phone can
    // carry different dial codes. Length is now validated per-country by PhoneInputComponent
    // itself (see its Validator.validate()) rather than a flat maxLength here.
    phone: new FormControl<PhoneValue>({ ...EMPTY_PHONE_VALUE }, { nonNullable: true }),
    mobilePhone: new FormControl<PhoneValue>({ ...EMPTY_PHONE_VALUE }, { nonNullable: true }),
    description: new FormControl('', { validators: Validators.maxLength(500), nonNullable: true }),
    isActive: new FormControl(true, { nonNullable: true })
  });

  partyControl = new FormControl('', { nonNullable: true });
  partySearchState$ = of(EMPTY_PARTY_SEARCH);

  // Office is a search box rather than a <select> because there are far too many branch offices
  // to scroll: the term is matched against city, office name, country, country code and address.
  // Function stays a native select — a handful of entries and no icons to show. Product is its
  // own custom listbox below (see productOpen) purely to show an icon per option: a native
  // <option> can never render one, in any browser.
  officeSearch = new FormControl('', { nonNullable: true });
  officeOptions: OfficeOption[] = [];
  officeOpen = false;
  // True while a term is narrowing the list. Browsing shows a plain list of cities (the same
  // reading as the native picker it replaced); searching adds the office/address line so a row
  // that matched on its address explains why.
  officeFiltered = false;

  productOpen = false;

  products: LookupOption[] = [];
  functions: LookupOption[] = [];
  assignments: AssignableParty[] = [];

  // Doubles as "still loading contact data" in edit mode, where it also gates on the
  // GET /supportContacts/{id} call loadForEdit() joins alongside the lookups fetch.
  loadingLookups = true;
  lookupsFailed = false;
  saving = false;

  // True when this dialog was opened from ContactsComponent.editContact() rather than
  // createContact() — swaps title/button copy and routes save() to the update endpoint.
  isEditMode = false;

  // Echoed back unchanged on update — this dialog has no UI for either, same as create always
  // posting both false (see the comment in save()). Only meaningful once loadForEdit() resolves.
  private existingShowMobilePhone = false;
  private existingShowPhoto = false;

  // Index of the row the arrow keys have moved to, in officeOptions / productOptions /
  // partyResults; -1 is "nothing highlighted". Replacing the native <select> means replacing its
  // keyboard support too, so all three of this dialog's result lists are arrow-navigable.
  officeHighlight = -1;
  productHighlight = -1;
  partyHighlight = -1;
  // Mirrors the parties the async pipe is currently rendering, so the keydown handler can act on
  // the highlighted one. Same tap-into-a-field pattern PartySelectorComponent uses.
  partyResults: AssignableParty[] = [];
  allAvailableColumns = [
    {
      key: 'search.message',
      name: 'Filter by message'
    },
    {
      key: 'customName',
      name: 'Name'
    },
    {
      key: 'email',
      name: 'Email'
    },
    {
      key: 'displayName',
      name: 'Name'
    },
    {
      key: 'mobilePhone',
      name: 'Mobile phone'
    },
    {
      key: 'phone',
      name: 'Business phone'
    },
    {
      key: 'description',
      name: 'Description'
    },
    {
      key: 'address',
      name: 'Address'
    }
  ];
  private allOffices: BranchOffice[] = [];
  private branchOfficesById = new Map<number, BranchOffice>();
  private productsById = new Map<number, LookupOption>();
  private functionsById = new Map<number, LookupOption>();
  private existingNames = new Set<string>();

  private lookupsService = inject(SupportContactLookupsService);
  private contactsService = inject(ContactsService);
  private partyService = inject(PartyService);
  private matSnackBar = inject(MatSnackBar);
  private destroyRef = inject(DestroyRef);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private matDialogRef = inject(MatDialogRef<AddDepartmentContactComponent, AddDepartmentContactResult>);
  private data = inject<AddDepartmentContactData | null>(MAT_DIALOG_DATA, { optional: true });
  private errorHandler = inject(ErrorHandlerService);

  ngOnInit() {
    this.existingNames = new Set((this.data?.existingNames ?? []).map(name => this.normalizeName(name)));
    this.isEditMode = !!this.data?.editContact;
    if (this.isEditMode) {
      this.loadForEdit();
    } else {
      this.loadLookups();
      if (this.data?.preselectedParty) {
        this.assignments = [this.data.preselectedParty];
      }
    }
    this.watchOfficeSearch();
    this.watchPartySearch();
    this.watchUseCustomName();
  }

  // --- Department name ---

  get selectedOffice(): BranchOffice | null {
    const id = this.departmentForm.controls.branchOfficeId.value;
    return id === null ? null : (this.branchOfficesById.get(id) ?? null);
  }

  // "Air Export Sydney" — product, then function, then the office's city, skipping whatever
  // has not been picked. Only the office is mandatory, so the name can legitimately be just a
  // city until the admin adds the other two parts.
  get builtName(): string {
    const parts = [
      this.lookupName(this.productsById, this.departmentForm.controls.productId.value),
      this.lookupName(this.functionsById, this.departmentForm.controls.functionId.value),
      this.selectedOffice?.city ?? ''
    ];
    return parts.filter(part => part.length > 0).join(' ');
  }

  get effectiveName(): string {
    return this.departmentForm.controls.useCustomName.value
      ? this.departmentForm.controls.customName.value.trim()
      : this.builtName;
  }

  get address(): string {
    return this.selectedOffice?.address ?? '';
  }

  // Advisory only — a near-duplicate name is unusual but not forbidden, and the server stays
  // the authority (a real conflict comes back as a 409 and is surfaced by errorMessageFor).
  get isDuplicateName(): boolean {
    const name = this.effectiveName;
    return name.length > 0 && this.existingNames.has(this.normalizeName(name));
  }

  // Live validation: every error below reflects the form's current state, not whether Create
  // has been clicked — see isFormValid, which the Create button's [disabled] is bound to.

  // Only meaningful in custom-name mode; built-name mode's equivalent is
  // showProductOrFunctionRequiredError below, since a built name is never "empty" on its own
  // (office alone contributes the city).
  get showNameRequiredError(): boolean {
    return (
      this.departmentForm.controls.useCustomName.value &&
      this.departmentForm.controls.customName.value.trim().length === 0
    );
  }

  // Built-name mode only: a name built from the office's city alone reads as just a place, not a
  // department, so at least one of Product/Function is mandatory too — either one alone is
  // enough to build a name with Office.
  get showProductOrFunctionRequiredError(): boolean {
    return (
      !this.departmentForm.controls.useCustomName.value &&
      this.departmentForm.controls.productId.value === null &&
      this.departmentForm.controls.functionId.value === null
    );
  }

  get showOfficeRequiredError(): boolean {
    return this.departmentForm.controls.branchOfficeId.value === null;
  }

  get emailError(): string | null {
    return this.departmentForm.controls.email.hasError('email') ? 'Incorrect email format' : null;
  }

  // Email alone is not required — at least one of email/phone/mobilePhone must be present,
  // same rule the API enforces. Only shown once emailError has nothing to say, so a malformed
  // email doesn't also get told it's "missing".
  get showContactMethodError(): boolean {
    const { email, phone, mobilePhone } = this.departmentForm.getRawValue();
    return (
      !this.emailError &&
      email.trim().length === 0 &&
      phone.number.trim().length === 0 &&
      mobilePhone.number.trim().length === 0
    );
  }

  get phoneError(): string | null {
    return this.phoneFieldError(this.departmentForm.controls.phone, 'Business phone');
  }

  get mobilePhoneError(): string | null {
    return this.phoneFieldError(this.departmentForm.controls.mobilePhone, 'Mobile phone');
  }

  get descriptionError(): string | null {
    return this.departmentForm.controls.description.hasError('maxlength')
      ? 'Description must be at most 500 characters'
      : null;
  }

  // Drives the Create/Save button's [disabled] — the button stays disabled until every rule
  // above is satisfied, so the admin never submits a request the API would just reject.
  // isDuplicateName is deliberately excluded: it is advisory only (see its getter).
  get isFormValid(): boolean {
    return (
      this.departmentForm.valid &&
      !this.showNameRequiredError &&
      !this.showProductOrFunctionRequiredError &&
      !this.showContactMethodError
    );
  }

  // --- Office picker ---

  // What the input shows once an office is picked. City alone would be ambiguous across
  // countries (there is more than one Portland), so the country rides along — the *built name*
  // still uses the city on its own.
  officeLabel(office: BranchOffice): string {
    return `${office.city}, ${office.country}`;
  }

  private officeSubtitle(office: BranchOffice): string {
    return [office.officeName, office.address].filter(part => !!part).join(' · ');
  }

  openOffices(event: FocusEvent) {
    this.officeOpen = true;
    // Select the existing text so typing replaces the previous pick. Without this, focusing a
    // box already reading "Sydney, Australia" and typing would search for that whole string plus
    // the new character and match nothing, forcing the admin to clear it by hand first.
    (event.target as HTMLInputElement).select();
  }

  // Runs on blur. Anything the admin typed but never picked is discarded and the box is put back
  // to the selected office's label (or emptied), so the text can never disagree with the id the
  // form actually holds.
  closeOffices() {
    this.officeOpen = false;
    this.officeHighlight = -1;
    const office = this.selectedOffice;
    this.officeSearch.setValue(office ? this.officeLabel(office) : '', { emitEvent: false });
    this.officeFiltered = false;
    this.officeOptions = this.buildOfficeOptions(this.allOffices);
  }

  selectOffice(office: BranchOffice) {
    this.departmentForm.controls.branchOfficeId.setValue(office.id);
    // emitEvent: false — this is the one write that must NOT clear the selection it just made.
    this.officeSearch.setValue(this.officeLabel(office), { emitEvent: false });
    this.officeOpen = false;
    this.officeHighlight = -1;
    this.officeFiltered = false;
    this.officeOptions = this.buildOfficeOptions(this.allOffices);
  }

  onOfficeKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.officeOpen = true;
      this.officeHighlight = this.moveHighlight(this.officeHighlight, this.officeOptions.length, event.key);
      this.revealOption('dept-office-opt-', this.officeHighlight);
      return;
    }
    if (event.key === 'Enter' && this.officeOpen) {
      const option = this.officeOptions[this.officeHighlight];
      if (option) {
        // Only swallow Enter when it actually picks something, so it still submits otherwise.
        event.preventDefault();
        this.selectOffice(option.office);
      }
      return;
    }
    if (event.key === 'Escape') {
      this.closeOffices();
    }
  }

  // --- Product picker ---

  get selectedProduct(): LookupOption | null {
    const id = this.departmentForm.controls.productId.value;
    return id === null ? null : (this.productsById.get(id) ?? null);
  }

  toggleProductOpen() {
    this.productOpen = !this.productOpen;
    if (!this.productOpen) {
      this.productHighlight = -1;
    }
  }

  closeProducts() {
    this.productOpen = false;
    this.productHighlight = -1;
  }

  selectProduct(product: LookupOption | null) {
    this.departmentForm.controls.productId.setValue(product?.id ?? null);
    this.closeProducts();
  }

  // Mirrors onOfficeKeydown: index 0 is the "Select..." row, so highlight indices are offset by
  // one against this.products (moveHighlight/revealOption only care about a count and an index,
  // not what they mean).
  onProductKeydown(event: KeyboardEvent) {
    const count = this.products.length + 1;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.productOpen = true;
      this.productHighlight = this.moveHighlight(this.productHighlight, count, event.key);
      this.revealOption('dept-product-opt-', this.productHighlight);
      return;
    }
    if (event.key === 'Enter' && this.productOpen && this.productHighlight >= 0) {
      // Only swallow Enter when it actually picks something, so it still submits otherwise.
      event.preventDefault();
      this.selectProduct(this.productHighlight === 0 ? null : this.products[this.productHighlight - 1]);
      return;
    }
    if (event.key === 'Escape') {
      this.closeProducts();
    }
  }

  // Un-ticking clears whatever was typed; ticking never touches productId/functionId, so
  // Product and Function stay exactly as picked until save() regardless of how this is toggled.
  private watchUseCustomName() {
    this.departmentForm.controls.useCustomName.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(useCustomName => {
        if (!useCustomName) {
          this.departmentForm.controls.customName.setValue('');
        }
        // In edit mode, this can land in the same coalesced-event window zone.js's eventCoalescing
        // (app.config.ts) already used to flush the async patchValue() calls loadForEdit() makes
        // on open — without forcing a check here, the Name preview and the custom-name box (both
        // driven straight off these controls' values in the template) can silently miss a render
        // and lag one keystroke/toggle behind what the form actually holds.
        this.changeDetectorRef.detectChanges();
      });

    // Same gap, for what the Name preview shows while typing a custom name — see the comment
    // above.
    this.departmentForm.controls.customName.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.changeDetectorRef.detectChanges());
  }

  // --- Customer assignment ---

  onPartyKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.partyHighlight = this.moveHighlight(this.partyHighlight, this.partyResults.length, event.key);
      this.revealOption('dept-party-opt-', this.partyHighlight);
      return;
    }
    if (event.key === 'Enter') {
      const party = this.partyResults[this.partyHighlight];
      if (party) {
        event.preventDefault();
        this.addAssignment(party);
      }
    }
  }

  addAssignment(party: AssignableParty) {
    if (this.assignments.some(assignment => assignment.id === party.id)) {
      return;
    }
    this.assignments = [...this.assignments, party];
    // Clearing the term also collapses the results dropdown, since it renders off this stream.
    this.partyControl.setValue('');
  }

  removeAssignment(id: number) {
    this.assignments = this.assignments.filter(assignment => assignment.id !== id);
  }

  clearAssignments() {
    this.assignments = [];
  }

  // --- Save ---

  save() {
    // Defensive only — the Create/Save button is disabled while isFormValid is false, so this
    // should never actually trip.
    if (!this.isFormValid) {
      this.departmentForm.markAllAsTouched();
      return;
    }

    const form = this.departmentForm.getRawValue();
    const common = {
      displayName: this.effectiveName,
      email: form.email.trim(),
      phone: this.orNull(form.phone.number),
      phoneCountryId: form.phone.countryId,
      mobilePhone: this.orNull(form.mobilePhone.number),
      mobilePhoneCountryId: form.mobilePhone.countryId,
      jobTitle: null,
      photoReference: null,
      address: this.orNull(this.address),
      // The parts the name was built from, so the server keeps them alongside the resolved name.
      productId: form.productId,
      functionId: form.functionId,
      branchOfficeId: form.branchOfficeId,
      description: this.orNull(form.description),
      isActive: form.isActive,
      // partyId, not the customer number — see PartyAssignment. Order is the list's own order.
      assignments: this.assignments.map((assignment, index) => ({
        partyId: assignment.id,
        role: null,
        sortOrder: index
      }))
    };

    this.saving = true;
    const request$ = this.isEditMode
      ? this.contactsService.updateDepartmentContact(this.data!.editContact!.id, {
          ...common,
          // No UI for either in this dialog — preserve whatever the contact already had rather
          // than clobbering it on every save.
          showMobilePhone: this.existingShowMobilePhone,
          showPhoto: this.existingShowPhoto
        })
      : this.contactsService.createDepartmentContact({
          ...common,
          type: 'Department',
          // A Department is never linked to a user; sending anything else is a 400.
          userId: null,
          // No UI for either: departments have no photo, and the existing department form has
          // always posted both as false.
          showMobilePhone: false,
          showPhoto: false
        } satisfies CreateDepartmentContactRequest);

    request$
      .pipe(
        tap(contact => {
          this.matSnackBar.open(this.isEditMode ? 'Department contact updated' : 'Department contact added', 'x', {
            panelClass: 'snackbar-green'
          });
          this.matDialogRef.close(
            this.isEditMode
              ? {
                  action: 'Updated',
                  // Everything the grid's row could show, built from the same values just sent
                  // to the server — lets ContactsComponent patch the row in place rather than
                  // refetching the whole grid to pick up a change it already knows about.
                  contact: {
                    id: this.data!.editContact!.id,
                    displayName: common.displayName,
                    email: common.email,
                    phone: form.phone.number.trim(),
                    phoneCountryId: form.phone.countryId,
                    mobilePhone: form.mobilePhone.number.trim(),
                    mobilePhoneCountryId: form.mobilePhone.countryId,
                    isActive: common.isActive,
                    productId: common.productId,
                    functionId: common.functionId,
                    branchOfficeId: common.branchOfficeId,
                    description: common.description
                  }
                }
              : // The server-assigned id/etc. — needed by the Organisations Detail screen's
                // "Create a new department…" fallback to add a row locally without a refetch
                // (essential for a brand-new, not-yet-persisted organisation).
                { action: 'Added', contact }
          );
        }),
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          this.saving = false;
          this.errorHandler.showApiError(
            error,
            this.errorMessageFor(error),
            this.allAvailableColumns,
            (fieldName, options) => options.find(x => x.key === fieldName)?.name
          );
          return NEVER;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  retryLookups() {
    if (this.isEditMode) {
      this.loadForEdit();
    } else {
      this.loadLookups();
    }
  }

  // --- Internals ---

  private loadLookups() {
    this.loadingLookups = true;
    this.lookupsFailed = false;

    this.lookupsService
      .getLookups()
      .pipe(
        tap(lookups => {
          this.applyLookups(lookups);
          this.loadingLookups = false;
        }),
        catchError(error => {
          console.error(error);
          this.loadingLookups = false;
          this.lookupsFailed = true;
          return NEVER;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  // Edit mode needs the same lookups (to render the pickers) plus the contact's own detail
  // record (email, phone, assignments, ...) — joined into one request so the dialog shows a
  // single loading/error state instead of two independent ones.
  private loadForEdit() {
    const seed = this.data!.editContact!;
    this.loadingLookups = true;
    this.lookupsFailed = false;

    forkJoin({
      lookups: this.lookupsService.getLookups(),
      contact: this.contactsService.getContactForEdit(seed.id)
    })
      .pipe(
        tap(({ lookups, contact }) => {
          this.applyLookups(lookups);
          this.applyEditContact(seed, contact);
          this.loadingLookups = false;
        }),
        catchError(error => {
          console.error(error);
          this.loadingLookups = false;
          this.lookupsFailed = true;
          return NEVER;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private applyLookups(lookups: SupportContactLookups) {
    this.products = lookups.products;
    this.functions = lookups.functions;
    this.allOffices = lookups.branchOffices;
    this.officeOptions = this.buildOfficeOptions(lookups.branchOffices);
    this.branchOfficesById = new Map(lookups.branchOffices.map(office => [office.id, office]));
    this.productsById = new Map(lookups.products.map(product => [product.id, product]));
    this.functionsById = new Map(lookups.functions.map(fn => [fn.id, fn]));
  }

  // Requires applyLookups() to have already run — builtName (used below to detect a custom
  // name) reads from productsById/functionsById/branchOfficesById.
  private applyEditContact(seed: DepartmentEditSeed, contact: EditContactDetails) {
    this.existingShowMobilePhone = contact.showMobilePhone;
    this.existingShowPhoto = contact.showPhoto;

    this.departmentForm.patchValue({
      productId: seed.productId,
      functionId: seed.functionId,
      branchOfficeId: seed.branchOfficeId,
      email: contact.email,
      phone: { countryId: contact.phoneCountryId ?? null, number: contact.phone ?? '' },
      mobilePhone: { countryId: contact.mobilePhoneCountryId ?? null, number: contact.mobilePhone ?? '' },
      description: seed.description ?? '',
      isActive: contact.isActive
    });

    // The office picker's text box is normally set by selectOffice()/closeOffices() as a side
    // effect of picking one — patching branchOfficeId directly above bypasses that, so mirror
    // it here now that the office lookup map is available.
    const office = this.selectedOffice;
    this.officeSearch.setValue(office ? this.officeLabel(office) : '', { emitEvent: false });

    // No explicit "is this a custom name" flag comes back from the API — infer it by comparing
    // the saved name against what the three parts build today. A mismatch means either the name
    // was entered manually, or the office/product/function has since been renamed.
    const useCustomName = contact.displayName.trim() !== this.builtName.trim();
    this.departmentForm.patchValue({
      useCustomName,
      customName: useCustomName ? contact.displayName : ''
    });

    this.assignments = [...contact.assignments]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(
        ({ partyId, partyNumber, partyName }): AssignableParty => ({
          id: partyId,
          number: partyNumber,
          name: partyName
        })
      );
  }

  // The office list is filtered entirely client-side: all offices arrive in the one cached
  // lookups call, so typing never costs a request.
  private watchOfficeSearch() {
    this.officeSearch.valueChanges
      .pipe(
        tap(term => {
          // Only reached when the admin types — selectOffice()/closeOffices() write the label
          // back with emitEvent: false. Editing the text abandons any previous pick, so the id
          // is cleared until something is chosen again.
          this.departmentForm.controls.branchOfficeId.setValue(null);
          this.officeFiltered = term.trim().length > 0;
          this.officeOptions = this.buildOfficeOptions(this.filterOffices(term));
          this.officeHighlight = -1;
          this.officeOpen = true;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private filterOffices(term: string): BranchOffice[] {
    const value = term.trim().toLowerCase();
    if (!value) {
      return this.allOffices;
    }
    return this.allOffices.filter(office =>
      [office.city, office.officeName, office.country, office.countryCode, office.address].some(field =>
        (field ?? '').toLowerCase().includes(value)
      )
    );
  }

  // branchOffices arrive sorted by region/country/city, so consecutive runs of the same country
  // are already adjacent — this only has to mark where each run starts. Filtering preserves that
  // order, so the headers stay correct on a filtered list too.
  private buildOfficeOptions(offices: BranchOffice[]): OfficeOption[] {
    let previousCountry = '';
    return offices.map(office => {
      const countryHeader = office.country === previousCountry ? null : office.country;
      previousCountry = office.country;
      return { office, countryHeader, subtitle: this.officeSubtitle(office) };
    });
  }

  // Wraps at both ends, and leaves the highlight cleared when there is nothing to move over.
  private moveHighlight(current: number, count: number, key: string): number {
    if (count === 0) {
      return -1;
    }
    const step = key === 'ArrowDown' ? 1 : -1;
    return (current + step + count) % count;
  }

  // Keeps the arrow-key highlight inside the panel's scroll window — without this the selection
  // walks off the bottom of a long office list invisibly. The rows are already in the DOM (only
  // their class changes), and they carry the same ids aria-activedescendant points at, so this
  // can look them up directly instead of holding a ViewChildren list purely to scroll one node.
  private revealOption(idPrefix: string, index: number) {
    if (index < 0) {
      return;
    }
    document.getElementById(`${idPrefix}${index}`)?.scrollIntoView({ block: 'nearest' });
  }

  private watchPartySearch() {
    this.partySearchState$ = this.partyControl.valueChanges.pipe(
      startWith(''),
      debounceTime(PARTY_SEARCH_DEBOUNCE_MS),
      distinctUntilChanged(),
      switchMap(query => {
        const term = query.trim();
        if (term.length < MIN_PARTY_SEARCH_LENGTH) {
          return of(EMPTY_PARTY_SEARCH);
        }

        return this.partyService.getParties(term).pipe(
          map(parties => {
            // Everything is keyed on the party id here, not the customer number: it is what the
            // assignments payload posts, so a party without one can't be assigned at all.
            // /parties can also repeat a party, hence the de-duplicate before filtering out the
            // ones already assigned, so a repeat never sneaks past.
            const assigned = new Set(this.assignments.map(assignment => assignment.id));
            const unique = new Map(parties.filter(isAssignable).map(party => [party.id, party]));
            return {
              parties: [...unique.values()].filter(party => !assigned.has(party.id)),
              loading: false,
              searched: true,
              error: false
            };
          }),
          startWith<PartySearchState>({ parties: [], loading: true, searched: true, error: false }),
          catchError(error => {
            this.errorHandler.showApiError(
              error,
              'Could not load Search parties by name or customer number, please try again',
              this.allAvailableColumns,
              (fieldName, options) => options.find(x => x.key === fieldName)?.name
            );

            return of<PartySearchState>({ parties: [], loading: false, searched: true, error: true });
          })
        );
      }),
      // Keep the keydown handler's view of the list in step with what is on screen, and drop the
      // highlight whenever the results change underneath it.
      tap(state => {
        this.partyResults = state.parties;
        this.partyHighlight = -1;
      })
    );
  }

  private lookupName(source: Map<number, LookupOption>, id: number | null): string {
    return id === null ? '' : (source.get(id)?.name ?? '');
  }

  // The API documents its optional fields as "null or omit". An empty string is a value rather
  // than an absence, so a field the admin left blank goes out as null.
  private orNull(value: string): string | null {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  // Surfaces the errors PhoneInputComponent's own Validator sets on the control (see
  // phone-input.component.ts's validate()) — this dialog only needs to turn the error keys into
  // copy, not decide when they apply.
  private phoneFieldError(control: FormControl<PhoneValue>, label: string): string | null {
    if (control.hasError('countryRequired')) {
      return `Select a country code for the ${label.toLowerCase()}`;
    }
    if (control.hasError('phoneNumberRequired')) {
      return `Enter the ${label.toLowerCase()} number`;
    }
    return null;
  }

  private normalizeName(name: string): string {
    return name.trim().replace(/\s+/g, ' ').toLowerCase();
  }

  private errorMessageFor(error: HttpErrorResponse): string {
    // A validation failure carries per-field messages ({ title, errors: [{ field, message }] }).
    // They are written for a human, so show them rather than a generic line — that is the
    // difference between "check the form" and "Description must be at most 500 characters."
    const fieldErrors: unknown = error.error?.errors;
    if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
      return fieldErrors.map((entry: { message?: string }) => entry.message).join(' ');
    }
    if (error.error?.code === 'PartyNotFound') {
      return 'One of the assigned parties no longer exists. Remove it and try again.';
    }
    if (error.status === 409) {
      return 'A contact with this name already exists.';
    }
    return 'Could not add a new contact. Please try again.';
  }
}
